import ClauseTypeType from '../store/schema/ClauseTypeType';
import getClauseIdsForSectionType from '../actions/helpers/getClauseIdsForSectionType';
import getClauseSectionProperties from '../actions/helpers/getClauseSectionProperties';
import { MAX_MESSAGE_SIZE } from '../store/schema/ClauseValueType';
import InboxRuleFolderType from '../store/schema/InboxRuleFolderType';
import store from '../store/store';
import ClauseSectionType from 'owa-inbox-rules-option/lib/actions/types/ClauseSectionType';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import getExistingClauseTypesInSection from '../actions/helpers/getExistingClauseTypesInSection';

export default function isValidClause(clauseId: string): boolean {
    let clause = store.inboxRulesEditState.clauseMap.get(clauseId);
    let clauseValue = clause.clauseValue;

    // if clause type hasn't been selected from dropdown or value hasn't been input yet
    if (!clause.clauseType) {
        return false;
    }

    switch (clause.clauseType) {
        case 'from':
        case 'to':
        case 'forwardTo':
        case 'forwardAsAttachmentTo':
        case 'redirectTo':
            // check if recipient well is empty
            let recipientWell = <RecipientWellWithFindControlViewState>clauseValue;
            return recipientWell.recipients.length != 0;
        case 'category':
        case 'subjectIncludes':
        case 'subjectOrBodyIncludes':
        case 'inTheBody':
        case 'inTheSenderAddress':
        case 'inTheRecipientAddress':
        case 'inTheMessageHeader':
            // check if includes words input/category list is empty
            let values = <string[]>clauseValue;
            return values.length != 0;
        case 'moveTo':
        case 'copyTo':
            // check if folder has been selected
            let selectedFolder = <InboxRuleFolderType>clauseValue;
            return selectedFolder != null && selectedFolder.folderId != null;
        case 'atLeast':
        case 'atMost':
            let messageSize = <number>clauseValue;
            return isValidMesssageSize(messageSize) && isAtLeastAtMostCompatible(clauseId);
        case 'fromTheAccount':
        case 'withImportance':
        case 'markImportance':
        case 'sensitivity':
        case 'classification':
        case 'flag':
        case 'type':
            return clauseValue != null;
        case 'onTheToOrCcLine':
        case 'theOnlyRecipientListed':
        case 'onTheToLine':
        case 'onTheCcLine':
        case 'notOnTheToLine':
        case 'delete':
        case 'pin':
        case 'read':
        case 'junk':
        case 'attachment':
        case 'before':
        case 'after':
        case 'any':
            return true;
        default:
            throw new Error('Cannot validate unknown clause type');
    }
}

export function isValidSection(sectionType: ClauseSectionType) {
    let sectionProperties = getClauseSectionProperties(sectionType);
    let clauseIds = getClauseIdsForSectionType(sectionType);
    let isValidClauseInSection = false;
    // Valid sections:
    // 1: Required sections with an unselected clause with at least one other valid clause in the section
    // 2: Optional sections with an unselected clause type
    // 3: Sections with all valid clauses
    for (let clauseId of clauseIds) {
        let clause = store.inboxRulesEditState.clauseMap.get(clauseId);
        if (!clause.clauseType && (isValidClauseInSection || sectionProperties.isOptional)) {
            return true;
        }
        if (!isValidClause(clauseId)) {
            return false;
        }
        isValidClauseInSection = true;
    }
    // we've checked every clause by this point
    return true;
}

export function isValidMesssageSize(messageSize: number) {
    return !isNaN(messageSize) && messageSize <= MAX_MESSAGE_SIZE && messageSize > 0;
}

export function isAtLeastAtMostCompatible(clauseId: string): boolean {
    let clause = store.inboxRulesEditState.clauseMap.get(clauseId);
    let atLeastValue;
    let atMostValue;
    let otherClausesInSection: Map<ClauseTypeType, string>;

    let conditionClauseIds = getClauseIdsForSectionType('conditional');

    // get the other clauses in the section, to check the other message size value
    if (conditionClauseIds.indexOf(clauseId) > -1) {
        otherClausesInSection = getExistingClauseTypesInSection('conditional');
    } else {
        otherClausesInSection = getExistingClauseTypesInSection('exception');
    }

    let correspondingId =
        clause.clauseType == 'atLeast'
            ? otherClausesInSection.get('atMost')
            : otherClausesInSection.get('atLeast');
    // if there is no other message size value, this message size is valid
    if (!correspondingId) {
        return true;
    }

    if (clause.clauseType == 'atLeast') {
        atLeastValue = clause.clauseValue;
        atMostValue = store.inboxRulesEditState.clauseMap.get(correspondingId).clauseValue;
    } else {
        atLeastValue = store.inboxRulesEditState.clauseMap.get(correspondingId).clauseValue;
        atMostValue = clause.clauseValue;
    }
    // check if the other message size
    return (
        isValidMesssageSize(atLeastValue) &&
        isValidMesssageSize(atMostValue) &&
        Number(atLeastValue) <= Number(atMostValue)
    );
}
